/**
 * @file DataLoadingDebugger.tsx
 * @description Debug panel component to track DICOM data loading issues
 * @author GynHub Team
 * @version 1.0.0
 */

import React, { useState, useEffect, useRef } from 'react';
import { 
  Bug, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Settings,
  ChevronDown,
  ChevronRight,
  Copy,
  Download
} from 'lucide-react';

interface DebugLogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'success';
  category: string;
  message: string;
  data?: any;
}

interface DataLoadingDebuggerProps {
  studyId?: string;
  patientId?: string;
  isVisible?: boolean;
  onToggle?: () => void;
}

// Global debug logger
class DebugLogger {
  private static instance: DebugLogger;
  private logs: DebugLogEntry[] = [];
  private listeners: Array<(logs: DebugLogEntry[]) => void> = [];
  
  static getInstance(): DebugLogger {
    if (!DebugLogger.instance) {
      DebugLogger.instance = new DebugLogger();
    }
    return DebugLogger.instance;
  }
  
  log(level: 'info' | 'warn' | 'error' | 'success', category: string, message: string, data?: any) {
    const entry: DebugLogEntry = {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data
    };
    
    this.logs.push(entry);
    
    // Keep only last 100 entries
    if (this.logs.length > 100) {
      this.logs = this.logs.slice(-100);
    }
    
    // Console logging with enhanced formatting
    const timestamp = new Date().toLocaleTimeString();
    const prefix = `[${timestamp}] [${category}]`;
    
    switch (level) {
      case 'error':
        console.error(`❌ ${prefix}`, message, data || '');
        break;
      case 'warn':
        console.warn(`⚠️ ${prefix}`, message, data || '');
        break;
      case 'success':
        console.log(`✅ ${prefix}`, message, data || '');
        break;
      default:
        console.log(`ℹ️ ${prefix}`, message, data || '');
    }
    
    // Notify listeners
    this.listeners.forEach(listener => listener([...this.logs]));
  }
  
  subscribe(listener: (logs: DebugLogEntry[]) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }
  
  clear() {
    this.logs = [];
    this.listeners.forEach(listener => listener([]));
  }
  
  getLogs() {
    return [...this.logs];
  }
}

// Export the global logger instance
export const debugLogger = DebugLogger.getInstance();

const DataLoadingDebugger: React.FC<DataLoadingDebuggerProps> = ({
  studyId,
  patientId,
  isVisible = false,
  onToggle
}) => {
  const [logs, setLogs] = useState<DebugLogEntry[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const logsEndRef = useRef<HTMLDivElement>(null);
  
  // Subscribe to debug logs
  useEffect(() => {
    const unsubscribe = debugLogger.subscribe(setLogs);
    setLogs(debugLogger.getLogs());
    return unsubscribe;
  }, []);
  
  // Auto scroll to bottom
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);
  
  // Log initial debug info
  useEffect(() => {
    if (studyId || patientId) {
      debugLogger.log('info', 'DEBUGGER', 'DataLoadingDebugger initialized', {
        studyId,
        patientId,
        timestamp: new Date().toISOString()
      });
    }
  }, [studyId, patientId]);
  
  const categories = ['all', ...Array.from(new Set(logs.map(log => log.category)))];
  const filteredLogs = selectedCategory === 'all' 
    ? logs 
    : logs.filter(log => log.category === selectedCategory);
  
  const getLogIcon = (level: string) => {
    switch (level) {
      case 'error': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'warn': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'success': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <Clock className="w-4 h-4 text-blue-500" />;
    }
  };
  
  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error': return 'text-red-600 bg-red-50';
      case 'warn': return 'text-yellow-600 bg-yellow-50';
      case 'success': return 'text-green-600 bg-green-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };
  
  const copyLogsToClipboard = () => {
    const logsText = filteredLogs.map(log => 
      `[${log.timestamp}] [${log.level.toUpperCase()}] [${log.category}] ${log.message}${
        log.data ? '\n' + JSON.stringify(log.data, null, 2) : ''
      }`
    ).join('\n\n');
    
    navigator.clipboard.writeText(logsText);
    debugLogger.log('success', 'DEBUGGER', 'Logs copied to clipboard');
  };
  
  const downloadLogs = () => {
    const logsText = filteredLogs.map(log => 
      `[${log.timestamp}] [${log.level.toUpperCase()}] [${log.category}] ${log.message}${
        log.data ? '\n' + JSON.stringify(log.data, null, 2) : ''
      }`
    ).join('\n\n');
    
    const blob = new Blob([logsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gynhub-debug-${new Date().toISOString().slice(0, 10)}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    debugLogger.log('success', 'DEBUGGER', 'Logs downloaded');
  };
  
  if (!isVisible) {
    return (
      <button
        onClick={onToggle}
        className="fixed bottom-4 right-4 z-50 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-lg transition-colors"
        title="Open Debug Panel"
      >
        <Bug className="w-5 h-5" />
      </button>
    );
  }
  
  return (
    <div className="fixed bottom-4 right-4 z-50 bg-white border border-gray-300 rounded-lg shadow-xl w-96 max-h-96 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center space-x-2">
          <Bug className="w-5 h-5 text-red-600" />
          <h3 className="font-semibold text-gray-900">Debug Panel</h3>
          <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">
            {logs.length} logs
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 text-gray-500 hover:text-gray-700"
            title="Toggle details"
          >
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <button
            onClick={copyLogsToClipboard}
            className="p-1 text-gray-500 hover:text-gray-700"
            title="Copy logs"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={downloadLogs}
            className="p-1 text-gray-500 hover:text-gray-700"
            title="Download logs"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={() => debugLogger.clear()}
            className="p-1 text-gray-500 hover:text-gray-700"
            title="Clear logs"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={onToggle}
            className="p-1 text-gray-500 hover:text-gray-700"
            title="Close"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Study/Patient Info */}
      <div className="p-2 bg-blue-50 border-b border-blue-200 text-xs">
        <div className="flex items-center space-x-4">
          {studyId && (
            <span className="text-blue-700">
              Study: <code className="bg-blue-100 px-1 rounded">{studyId}</code>
            </span>
          )}
          {patientId && (
            <span className="text-blue-700">
              Patient: <code className="bg-blue-100 px-1 rounded">{patientId}</code>
            </span>
          )}
        </div>
      </div>
      
      {/* Filters */}
      <div className="p-2 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-xs">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="text-xs border border-gray-300 rounded px-2 py-1"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat === 'all' ? 'All Categories' : cat}
              </option>
            ))}
          </select>
          <label className="flex items-center space-x-1">
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
              className="text-xs"
            />
            <span>Auto-scroll</span>
          </label>
        </div>
      </div>
      
      {/* Logs */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1 text-xs">
        {filteredLogs.length === 0 ? (
          <div className="text-center text-gray-500 py-4">
            No debug logs yet...
          </div>
        ) : (
          filteredLogs.map((log, index) => (
            <div key={index} className={`p-2 rounded border-l-2 ${getLevelColor(log.level)}`}>
              <div className="flex items-start space-x-2">
                {getLogIcon(log.level)}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="font-mono text-gray-500">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </span>
                    <span className="font-semibold">
                      [{log.category}]
                    </span>
                  </div>
                  <div className="mt-1 text-sm">
                    {log.message}
                  </div>
                  {isExpanded && log.data && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-gray-600 hover:text-gray-800">
                        Show data
                      </summary>
                      <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                        {JSON.stringify(log.data, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
};

export default DataLoadingDebugger;
